import { useEffect } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Alert, Box, Grid, Paper, Stack, Typography } from '@mui/material';
import { useCurrentUser } from '@diggens/user-management';
import { useAgentRuns, usePlanApprovals } from '@diggens/shared-hooks';
import { hiddenAgentsFor, type AgentSlug } from '@diggens/types';
import { PendingCard } from '../components/agents/PendingCard';
import { orderRuns, useNow } from '@diggens/chat';
import { RunList } from '../components/agents/RunList';
import { RunMonitor } from '../components/agents/RunMonitor';

const ADMIN_ROLES = ['admin', 'super_admin'];

/** Where an old /agents link goes now: this page, which holds its activity and the plans waiting for a decision. */
export function legacyAgentsTarget(search: string): string {
  const params = new URLSearchParams(search);
  params.delete('tab');
  const rest = params.toString();
  return rest ? `/activity?${rest}` : '/activity';
}

export function LegacyAgentsRedirect() {
  const { search } = useLocation();
  return <Navigate to={legacyAgentsTarget(search)} replace />;
}

/**
 * The page's state lives in the URL, so a run can be linked to, reloaded and reached with Back: ?run=, ?agent= to show
 * one agent's runs, and ?plan= from the chat.
 */
function Runs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: user } = useCurrentUser();
  // An agent the viewer is not shown is not one they can filter by, nor see named
  const hidden: readonly string[] = hiddenAgentsFor(user?.role);
  // A compiled agent's slug, or a persona's (any well-formed slug the viewer may see; the roster is the authority on which exist)
  const requested = searchParams.get('agent');
  const agent: AgentSlug | null = requested && /^[a-z][a-z0-9_]{1,39}$/.test(requested) && !hidden.includes(requested) ? requested : null;
  const { data: runs } = useAgentRuns();
  const { data: approvals } = usePlanApprovals();
  const now = useNow();

  // A link to a plan, like the chat's plan card, opens the run that proposed it; an older revision is found among the approvals
  const planId = searchParams.get('plan');
  const planRunId = planId
    ? (runs?.find((run) => run.plan?.planId === planId)?.runId ?? [...(approvals?.pending ?? []), ...(approvals?.decided ?? [])].find((approval) => approval.planId === planId)?.runId ?? null)
    : null;
  const planMissing = planId !== null && planRunId === null && runs !== undefined && approvals !== undefined;
  useEffect(() => {
    if (!planRunId) return;
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.delete('plan');
        next.set('run', planRunId);
        return next;
      },
      { replace: true }
    );
  }, [planRunId, setSearchParams]);

  // Until the admin picks a run, show the top of the list (one waiting for approval, else one running, else the newest),
  // pinned in the URL so the next poll's reordering never swaps the run being read
  const firstRunId = runs ? (orderRuns(runs.filter((run) => !agent || run.agents.includes(agent)))[0]?.runId ?? null) : null;
  const pinRunId = !searchParams.get('run') && !planId ? firstRunId : null;
  useEffect(() => {
    if (!pinRunId) return;
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.set('run', pinRunId);
        return next;
      },
      { replace: true }
    );
  }, [pinRunId, setSearchParams]);
  // A plan link that matches no run selects none, so the alert is not shown above an unrelated run
  const selectedRunId = searchParams.get('run') ?? planRunId ?? (planId ? null : firstRunId);

  const update = (change: (params: URLSearchParams) => void) =>
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      change(next);
      return next;
    });
  const selectRun = (runId: string) =>
    update((params) => {
      params.delete('plan');
      params.set('run', runId);
    });

  // A plan waiting for a decision gets its Approve and Reject from the run opened beside the list. One the list cannot lead
  // to, because the runs failed or have not loaded yet, or its run is past the newest 50, gets them here instead.
  const listed = new Set((runs ?? []).map((run) => run.runId));
  const unlisted = (approvals?.pending ?? []).filter((approval) => !listed.has(approval.runId) && approval.runId !== selectedRunId);

  return (
    <Grid container spacing={3}>
      {/* The list gets more room below 1200px, where the sidebar's labels would squeeze its titles */}
      <Grid size={{ xs: 12, md: 5, lg: 4 }}>
        <RunList selectedRunId={selectedRunId} onSelect={selectRun} agent={agent} onClearAgent={() => update((params) => params.delete('agent'))} />
      </Grid>
      <Grid size={{ xs: 12, md: 7, lg: 8 }}>
        {unlisted.length > 0 && (
          <Stack spacing={2} sx={{ mb: 2 }}>
            {unlisted.map((approval) => (
              <PendingCard key={approval.planId} approval={approval} now={now} onOpenRun={() => selectRun(approval.runId)} />
            ))}
          </Stack>
        )}
        {planMissing && (
          <Alert severity="info" sx={{ mb: 2 }}>
            That plan’s run is not among the last 7 days of runs.
          </Alert>
        )}
        {selectedRunId ? (
          <RunMonitor key={selectedRunId} runId={selectedRunId} />
        ) : (
          runs?.length === 0 && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No agent runs in the last 7 days. Ask your Chief of Staff to do something, and its work shows up here.</Typography>
            </Paper>
          )
        )}
      </Grid>
    </Grid>
  );
}

/** Everything the workspace's agents did over the last 7 days, one run at a time, with the plans waiting for a decision first. */
export function Activity() {
  const { data: user } = useCurrentUser();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4">Activity</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Every run your agents worked on in the last 7 days. Open one to see what was asked, its plan, each agent’s steps and the answer.
      </Typography>
      {user && (ADMIN_ROLES.includes(user.role) ? <Runs /> : <Alert severity="info">Only workspace admins can see what agents are working on.</Alert>)}
    </Box>
  );
}
